import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';

import type { BookingRecoveryCardSpec } from '../bookingRecovery';

interface Props {
  spec: BookingRecoveryCardSpec;
  /** El estado crudo (`provider_unknown_state → sync`), para soporte. */
  technicalCode: string;
  /** La referencia con la que el proveedor identifica la reserva. */
  reference?: string;
  /** Identificador local para consultar y conciliar; no es un localizador. */
  bookingId?: string;
  loading?: boolean;
  /**
   * EL botón de la tarjeta. Sin acción disponible (audiencia sin permiso para
   * resolverla) la tarjeta explica igual: un estado raro sin explicación es
   * peor que uno sin botón.
   */
  onAction?: () => void;
  /**
   * Por qué el CTA está deshabilitado y qué camino queda. Un botón gris jamás
   * va mudo: si la acción no se puede disparar desde acá, se dice y se nombra
   * la salida.
   */
  disabledNote?: string;
}

/**
 * Un estado raro del hold, con SU cara y SU acción.
 *
 * La regla es la de `docs/RUTERO.md` §5: un estado recuperable nunca se pinta
 * como error terminal. Por eso esta tarjeta es SIEMPRE ámbar y siempre trae la
 * acción segura de ese estado — el rojo queda para los rechazos limpios
 * (`HoldTerminalErrorCard`), donde lo único honesto es volver a buscar.
 *
 * El vocabulario es el del asesor ("no sabemos si llegó"), no el del sistema:
 * el código técnico queda chiquito y en mono, para soporte, y la acción en
 * grande.
 */
export function BookingRecoveryCard({
  spec,
  technicalCode,
  reference,
  bookingId,
  loading,
  onAction,
  disabledNote,
}: Props) {
  const { t } = useTranslation();

  return (
    <Alert severity="warning" icon={false}>
      <AlertTitle>{t(spec.titleKey)}</AlertTitle>
      <Typography variant="body2">{t(spec.bodyKey)}</Typography>
      {reference && (
        <Typography variant="caption" display="block">
          {t('travelServices.booking.recovery.providerReference', { reference })}
        </Typography>
      )}
      {bookingId && (
        <Typography variant="caption" display="block">
          {t('travelServices.booking.recovery.trackingId', { id: bookingId })}
        </Typography>
      )}
      {disabledNote && (
        <Typography variant="body2" mt={1}>
          {disabledNote}
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mt={1.5}>
        {(onAction || disabledNote) && (
          <LoadingButton
            size="small"
            color="warning"
            variant={spec.emphasis === 'primary' ? 'contained' : 'outlined'}
            loading={loading}
            disabled={!onAction}
            onClick={onAction}
          >
            {t(spec.ctaKey)}
          </LoadingButton>
        )}
        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
          {technicalCode}
        </Typography>
      </Box>
    </Alert>
  );
}
